/**
 * Enhanced Login Page
 * Premium design with animated gradients and glass materials
 */

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useAuthStore } from '../stores/authStore'
import Logo from '../components/Logo'
import { LockIcon, SparklesIcon } from '../components/icons'
import Badge from '../components/ui/Badge'

function extractAppleErrorDetails(err: unknown): string {
  if (!err) return 'unknown'
  if (typeof err === 'string') return err

  const e = err as Record<string, unknown>
  const fields = {
    error: e.error,
    error_description: e.error_description,
    message: e.message,
    code: e.code,
    status: e.status,
  }

  try {
    return JSON.stringify(fields)
  } catch {
    return String(err)
  }
}

const APPLE_AUTH_STATE_KEY = 'apple_auth_state'
const APPLE_AUTH_NONCE_KEY = 'apple_auth_nonce'

function setAppleStateCookie(state: string) {
  document.cookie = `apple_auth_state=${encodeURIComponent(state)}; Domain=.appstats.app; Path=/; Secure; SameSite=Lax`
}

function getAppleStateCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)apple_auth_state=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

function clearAppleStateCookie() {
  document.cookie = 'apple_auth_state=; Domain=.appstats.app; Path=/; Max-Age=0; Secure; SameSite=Lax'
}

function randomString(length = 32): string {
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('').slice(0, length)
}

export default function LoginPage() {
  const [isAppleLoading, setIsAppleLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { login } = useAuthStore()
  
  // Computed loading state for callback handlers
  const isLoading = isAppleLoading || isGoogleLoading

  // Handle redirect callback from direct Apple OAuth flow.
  useEffect(() => {
    if (window.location.hostname === 'www.appstats.app') {
      const target = `https://appstats.app${window.location.pathname}${window.location.search}${window.location.hash}`
      window.location.replace(target)
      return
    }

    const completeAppleCallback = async () => {
      if (window.location.pathname !== '/auth/apple/callback') return

      setIsAppleLoading(true)
      setError(null)

      try {
        const query = new URLSearchParams(window.location.search)
        const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))

        const payload = hash.toString().length > 0 ? hash : query
        const code = payload.get('code') ?? undefined
        const idToken = payload.get('id_token') ?? undefined
        const returnedState = payload.get('state')
        const authError = payload.get('error')
        const authErrorDescription = payload.get('error_description')
        const expectedState = sessionStorage.getItem(APPLE_AUTH_STATE_KEY) || getAppleStateCookie()

        if (authError) {
          throw new Error(`${authError}${authErrorDescription ? `: ${authErrorDescription}` : ''}`)
        }

        if (!code && !idToken) {
          throw new Error('Apple callback missing code/id_token')
        }

        // Dev relay: if the state contains a `dev` origin and we're on production,
        // forward the auth params to the local dev server instead of processing here.
        if (returnedState) {
          try {
            const parsed = JSON.parse(returnedState) as { s?: string; dev?: string }
            const devOrigin = parsed.dev
            const isOnProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
            if (devOrigin && isOnProduction) {
              const fwd = new URLSearchParams()
              if (code) fwd.set('code', code)
              if (idToken) fwd.set('id_token', idToken)
              fwd.set('state', returnedState)
              window.location.replace(`${devOrigin}/auth/apple/callback?${fwd.toString()}`)
              return
            }
          } catch {
            // Plain string state — no dev relay needed
          }
        }

        // Tolerate missing state sources across cross-origin/browser flows as long
        // as Apple returned a valid authorization payload. Keep strict mismatch check
        // only when both values are present and different.
        if (returnedState && expectedState && returnedState !== expectedState) {
          throw new Error('Apple callback state mismatch')
        }
        if (!expectedState) {
          console.warn('Apple callback state missing locally; proceeding with code exchange')
        } else if (!returnedState) {
          console.warn('Apple callback state missing in redirect; proceeding with code exchange')
        }

        await login(idToken, code)
        window.history.replaceState({}, document.title, '/')
      } catch (err: unknown) {
        const details = extractAppleErrorDetails(err)
        console.error('Apple callback error (raw):', err)
        console.error('Apple callback error (details):', details)
        setError(`Failed to sign in with Apple. ${details}`)
      } finally {
        sessionStorage.removeItem(APPLE_AUTH_STATE_KEY)
        sessionStorage.removeItem(APPLE_AUTH_NONCE_KEY)
        clearAppleStateCookie()
        setIsAppleLoading(false)
      }
    }

    const completeGoogleCallback = async () => {
      if (window.location.pathname !== '/auth/google/callback') return

      setIsGoogleLoading(true)
      setError(null)

      try {
        const query = new URLSearchParams(window.location.search)
        const code = query.get('code')
        const returnedState = query.get('state')
        const authError = query.get('error')
        const expectedState = sessionStorage.getItem('google_auth_state')

        if (authError) {
          throw new Error(`Google authentication error: ${authError}`)
        }

        if (!code) {
          throw new Error('Google callback missing authorization code')
        }

        if (returnedState !== expectedState) {
          throw new Error('Google callback state mismatch')
        }

        // Call backend to exchange code for tokens
        await login(undefined, code, undefined, 'google')
        window.history.replaceState({}, document.title, '/')
      } catch (err: unknown) {
        console.error('Google callback error:', err)
        setError(`Failed to sign in with Google. ${err}`)
      } finally {
        sessionStorage.removeItem('google_auth_state')
        setIsGoogleLoading(false)
      }
    }

    void completeAppleCallback()
    void completeGoogleCallback()
  }, [login])

  // Start direct Apple OAuth redirect flow (no popup/web_message).
  const handleAppleSignIn = async () => {
    setIsAppleLoading(true)
    setError(null)
    
    try {
      const clientId = import.meta.env.VITE_APPLE_CLIENT_ID || 'com.onethum.AppStats.web2'
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

      // Apple requires HTTPS redirect URIs. On localhost, relay through the production
      // callback URL and embed a dev marker in the state so production forwards
      // the auth code back to the local dev server automatically.
      const redirectURI = isLocalhost
        ? 'https://appstats.app/auth/apple/callback'
        : (import.meta.env.VITE_APPLE_REDIRECT_URI || 'https://appstats.app/auth/apple/callback')

      const statePayload = isLocalhost
        ? JSON.stringify({ s: randomString(24), dev: window.location.origin })
        : randomString(24)
      const nonce = randomString(24)

      sessionStorage.setItem(APPLE_AUTH_STATE_KEY, statePayload)
      sessionStorage.setItem(APPLE_AUTH_NONCE_KEY, nonce)
      setAppleStateCookie(statePayload)

      const authUrl = new URL('https://appleid.apple.com/auth/authorize')
      authUrl.searchParams.set('client_id', clientId)
      authUrl.searchParams.set('redirect_uri', redirectURI)
      // Keep the request minimal to avoid Apple rejecting hybrid token flows.
      // Backend exchanges the authorization code for tokens server-side.
      authUrl.searchParams.set('response_type', 'code')
      authUrl.searchParams.set('response_mode', 'query')
      authUrl.searchParams.set('state', statePayload)
      authUrl.searchParams.set('nonce', nonce)

      // Apple authorize URLs are sensitive to space encoding; use %20 instead of +
      // for response_type/scope values to match Apple's own SDK format.
      const authorizeUrl = authUrl.toString().replace(/\+/g, '%20')
      window.location.assign(authorizeUrl)
      return
    } catch (err: unknown) {
      const details = extractAppleErrorDetails(err)
      console.error('Apple Sign In error (raw):', err)
      console.error('Apple Sign In error (details):', details)

      if ((err as { error?: string })?.error === 'popup_closed_by_user') {
        setError('Sign in was cancelled')
      } else {
        setError(`Failed to sign in with Apple. ${details}`)
      }
    }

    if (window.location.pathname !== '/auth/apple/callback') {
      setIsAppleLoading(false)
    }
  }

  // Start Google OAuth redirect flow
  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true)
    setError(null)
    
    try {
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || 'your-google-client-id'
      const redirectURI = import.meta.env.VITE_GOOGLE_REDIRECT_URI || 'https://appstats.app/auth/google/callback'
      const state = randomString(24)

      sessionStorage.setItem('google_auth_state', state)

      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth')
      authUrl.searchParams.set('client_id', clientId)
      authUrl.searchParams.set('redirect_uri', redirectURI)
      authUrl.searchParams.set('response_type', 'code')
      authUrl.searchParams.set('scope', 'openid email profile')
      authUrl.searchParams.set('state', state)
      authUrl.searchParams.set('access_type', 'offline')
      authUrl.searchParams.set('prompt', 'select_account')

      window.location.assign(authUrl.toString())
      return
    } catch (err: unknown) {
      console.error('Google Sign In error:', err)
      setError(`Failed to sign in with Google. ${err}`)
    }

    if (window.location.pathname !== '/auth/google/callback') {
      setIsGoogleLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 overflow-hidden relative">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-black">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950" />
        
        {/* Animated gradient orbs */}
        <motion.div
          className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-purple-500/30 to-blue-500/30 rounded-full blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-3xl"
          animate={{
            x: [0, -100, 0],
            y: [0, -50, 0],
            scale: [1.2, 1, 1.2],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-96 h-96 bg-gradient-to-br from-indigo-500/20 to-pink-500/20 rounded-full blur-3xl"
          animate={{
            x: [-100, 100, -100],
            y: [-50, 50, -50],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Content */}
      <motion.div
        className="w-full relative z-10 flex flex-col items-center px-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Logo */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center mb-6"
          >
            <Logo size="xl" gradient />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <p className="text-gray-400 text-lg mb-6">
              Analytics for iOS & Android Developers
            </p>
          </motion.div>
        </motion.div>

        {/* Container for badges and login card with matching width */}
        <div className="flex flex-col items-center gap-6">
          {/* Trust badges */}
          <motion.div
            className="flex items-center justify-center gap-3 flex-wrap"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Badge variant="default" size="sm" dot>
              <LockIcon size="xs" />
              Privacy First
            </Badge>
            <Badge variant="default" size="sm" dot>
              <SparklesIcon size="xs" />
              Zero Config
            </Badge>
            <Badge variant="default" size="sm" dot>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline-block">
                <path d="M15.3636 17.5C14.3182 18.5909 13.1818 18.5 12.0909 18.0455C10.9545 17.5909 9.90909 17.5455 8.72727 18.0455C7.22727 18.6818 6.40909 18.5455 5.5 17.5C1.22727 12.9545 1.86364 6.13636 6.72727 5.86364C7.95455 5.90909 8.81818 6.5 9.54545 6.54545C10.6364 6.27273 11.6818 5.59091 12.8636 5.63636C14.2727 5.72727 15.3182 6.27273 16 7.31818C12.9091 9.13636 13.6364 13.1364 16.5455 14.3182C15.9545 15.8636 15.2273 17.3636 15.3636 17.5ZM9.45455 5.81818C9.31818 3.68182 11 1.90909 13 1.72727C13.2727 4.18182 10.7273 6.04545 9.45455 5.81818Z" fill="currentColor"/>
              </svg>
              Apple Native
            </Badge>
          </motion.div>

          {/* Login card - matches badge container width */}
          <motion.div
            className="glass-card-elevated p-8 w-full"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
          <h2 className="text-2xl font-semibold mb-6 text-center">
            Welcome Back
          </h2>

          {error && (
            <motion.div
              className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 mb-6"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="text-red-400 text-sm">{error}</p>
            </motion.div>
          )}

          {/* Official Apple Sign In Button */}
          <button
            onClick={handleAppleSignIn}
            disabled={isLoading}
            className="w-full bg-white text-black py-4 px-6 rounded-xl font-medium flex items-center justify-center gap-3 hover:bg-gray-100 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl border border-gray-200"
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}
          >
            {isAppleLoading ? (
              <>
                <motion.div
                  className="w-5 h-5 border-2 border-gray-300 border-t-black rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.3636 17.5C14.3182 18.5909 13.1818 18.5 12.0909 18.0455C10.9545 17.5909 9.90909 17.5455 8.72727 18.0455C7.22727 18.6818 6.40909 18.5455 5.5 17.5C1.22727 12.9545 1.86364 6.13636 6.72727 5.86364C7.95455 5.90909 8.81818 6.5 9.54545 6.54545C10.6364 6.27273 11.6818 5.59091 12.8636 5.63636C14.2727 5.72727 15.3182 6.27273 16 7.31818C12.9091 9.13636 13.6364 13.1364 16.5455 14.3182C15.9545 15.8636 15.2273 17.3636 15.3636 17.5ZM9.45455 5.81818C9.31818 3.68182 11 1.90909 13 1.72727C13.2727 4.18182 10.7273 6.04545 9.45455 5.81818Z" fill="currentColor"/>
                </svg>
                <span>Sign in with Apple</span>
              </>
            )}
          </button>

          {/* Official Google Sign In Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full bg-white text-gray-700 py-4 px-6 rounded-xl font-medium flex items-center justify-center gap-3 hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl border border-gray-300 mt-3"
            style={{ fontFamily: 'Roboto, arial, sans-serif' }}
          >
            {isGoogleLoading ? (
              <>
                <motion.div
                  className="w-5 h-5 border-2 border-gray-300 border-t-gray-700 rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  <path fill="none" d="M0 0h48v48H0z"/>
                </svg>
                <span>Sign in with Google</span>
              </>
            )}
          </button>

          {/* Removed mock authentication notice - production only uses real Sign In */}
        </motion.div>
        </div>

        {/* Footer */}
        <motion.div
          className="mt-8 text-center text-gray-500 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <p className="text-xs">© 2026 One Thum Software</p>
        </motion.div>
      </motion.div>
    </div>
  )
}
